import {
  constFunction,
  constNumber,
  constString,
  isAssignableTo,
  Node,
  NodeOrVoidNode,
  opFilter,
  opIndex,
  opIsNone,
  opNot,
  opNumberEqual,
  opPick,
  union,
  voidNode,
} from '@wandb/weave/core';
import React, {useMemo} from 'react';

import {useNodeWithServerType} from '../../../react';
import {usePanelStacksForType} from '../availablePanels';
import * as Panel2 from '../panel';
import {PanelStepper} from './component';
import {PanelStepperConfig} from './configComponent';
import {
  LIST_ANY_TYPE,
  PanelStepperConfigType,
  PanelStepperEntryProps,
  PanelStepperProps,
} from './types';
import {
  convertInputNode,
  getDefaultWorkingKeyAndType,
  getKeysAndTypesFromPropertyType,
  NONE_KEY_AND_TYPE,
} from './util';

const PanelStepperEntryComponent: React.FC<PanelStepperEntryProps> = props => {
  const {input, updateConfig, config, isConfigMode} = props;

  const safeUpdateConfig = React.useCallback(
    (updates: Partial<PanelStepperConfigType>) => {
      const needsUpdate = Object.entries(updates).some(
        ([key, value]) => (config as any)?.[key] !== value
      );

      if (needsUpdate) {
        updateConfig(updates);
      }
    },
    [config, updateConfig]
  );

  const convertedInputNode = convertInputNode(input as Node);
  const convertedInputNodeRefined = useNodeWithServerType(convertedInputNode);

  const propertyKeysAndTypes = useMemo(
    () => ({
      ...NONE_KEY_AND_TYPE,
      ...getKeysAndTypesFromPropertyType(convertedInputNodeRefined.result.type),
    }),
    [convertedInputNodeRefined.result.type]
  );

  const workingKeyAndType = getDefaultWorkingKeyAndType(
    config,
    propertyKeysAndTypes
  );

  if (config?.workingKeyAndType == null || config?.workingSliderKey == null) {
    safeUpdateConfig({
      workingKeyAndType,
      workingSliderKey: config?.workingSliderKey ?? '_step',
    });
  }

  const workingInputNode =
    workingKeyAndType.key === '<none>'
      ? convertedInputNode
      : opFilter({
          arr: convertedInputNode,
          filterFn: constFunction(
            {row: convertedInputNodeRefined.result.type},
            ({row}) =>
              opNot({
                bool: opIsNone({
                  val: opPick({
                    obj: row,
                    key: constString(workingKeyAndType.key),
                  }),
                }),
              })
          ),
        });

  const exampleRow = opIndex({
    arr: workingInputNode,
    index: constNumber(0),
  });
  const exampleRowRefined = useNodeWithServerType(exampleRow);
  let outputNode: NodeOrVoidNode = voidNode();
  if (config?.currentStep != null && config?.workingSliderKey) {
    const resultsAtStepNode = opFilter({
      arr: workingInputNode,
      filterFn: constFunction({row: exampleRowRefined.result.type}, ({row}) =>
        opNumberEqual({
          lhs: opPick({
            obj: row,
            key: constString(config.workingSliderKey),
          }),
          rhs: constNumber(config.currentStep),
        })
      ),
    });

    outputNode =
      workingKeyAndType.key === '<none>'
        ? resultsAtStepNode
        : opPick({
            obj: resultsAtStepNode,
            key: constString(workingKeyAndType.key),
          });
  }

  const outputNodeRefined = useNodeWithServerType(outputNode);

  const {stackIds, handler} = usePanelStacksForType(
    outputNodeRefined.result.type,
    config?.workingPanelId ?? undefined,
    {
      disallowedPanels: ['panel-stepper'],
    }
  );

  if (config?.workingPanelId == null) {
    safeUpdateConfig({
      workingPanelId: stackIds[0].id,
    });
  }

  const numericalKeys = Object.keys(propertyKeysAndTypes).filter(
    key =>
      isAssignableTo(propertyKeysAndTypes[key], union(['none', 'number'])) &&
      // Special filtering for metrics that could be integers but are not step related.
      !['loss', 'accuracy', 'precision', 'recall'].some(metric =>
        key.includes(metric)
      )
  );

  const additionalProps = {
    safeUpdateConfig,
    convertedInputNode,
    filteredNode: workingInputNode,
    outputNode: outputNodeRefined.result,
    propertyKeysAndTypes,
    childPanelStackIds: stackIds,
    childPanelHandler: handler,
  };

  const renderComponent = () => {
    if (config?.workingSliderKey == null || config?.workingSliderKey === '') {
      return (
        <div
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            fontSize: '14px',
          }}>
          Please enter a slider key (e.g. _step).
        </div>
      );
    }

    if (!numericalKeys.includes(config?.workingSliderKey ?? '')) {
      return (
        <div
          style={{
            height: '100%',
            width: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: '#666',
            fontSize: '14px',
          }}>
          {config?.workingSliderKey} is not a valid step metric. Please use a
          integral numeric property (e.g. _step).
        </div>
      );
    }

    return <PanelStepper {...props} {...additionalProps} />;
  };

  return isConfigMode ? (
    <PanelStepperConfig {...props} {...additionalProps} />
  ) : (
    renderComponent()
  );
};

const PanelStepperConfigComponent: React.FC<PanelStepperProps> = props => {
  return <PanelStepperEntryComponent {...props} isConfigMode={true} />;
};

const PanelStepperComponent: React.FC<PanelStepperProps> = props => {
  return <PanelStepperEntryComponent {...props} isConfigMode={false} />;
};

export const Spec: Panel2.PanelSpec<PanelStepperConfigType> = {
  id: 'panel-stepper',
  displayName: 'Stepper',
  Component: PanelStepperComponent,
  ConfigComponent: PanelStepperConfigComponent,
  inputType: LIST_ANY_TYPE,
};
